import React, { useState, useMemo, useEffect } from 'react'
import { motion } from 'framer-motion'
import { ShoppingCart, Layers, Check, ExternalLink, Loader2, RefreshCw, CheckCircle2, ChevronDown, Package, Search } from 'lucide-react'
import { SalesProduct, SalesFormatId } from '../../types'
import { MediaBuyData, CreativeSyncResult } from '../../lib/actionCable'
import { conversationApi } from '../../lib/api/conversations'
import { BrowseProductsModal } from './BrowseProductsModal'
import { CreateMediaBuyModal, CreateMediaBuyFormValues } from './CreateMediaBuyModal'
import toast from 'react-hot-toast'

/**
 * Extra fields the user supplies via the CreateMediaBuyModal. Forwarded
 * verbatim to the parent so it can decorate the API payload.
 */
export type CreateMediaBuyExtras = CreateMediaBuyFormValues

interface MediaBuyTabProps {
  products: SalesProduct[]
  conversationId: number | null
  /** Campaign ID from the URL; forwarded as enzymic_campaign_id when syncing creatives. */
  campaignId?: number | null
  onCreateMediaBuy: (
    selectedProducts: SalesProduct[],
    extras: CreateMediaBuyExtras,
  ) => Promise<void>
  isCreating?: boolean
  mediaBuyData?: MediaBuyData | null
  selectedProductIds?: string[]
  syncedCreatives?: CreativeSyncResult[]
  onSendMessage?: (message: string) => void
}

export function MediaBuyTab({ 
  products, 
  conversationId,
  campaignId = null,
  onCreateMediaBuy,
  isCreating = false,
  mediaBuyData = null,
  selectedProductIds: mediaBuyProductIds = [],
  syncedCreatives = [],
  onSendMessage
}: MediaBuyTabProps) {
  const [activeSubTab, setActiveSubTab] = useState<'products' | 'creatives'>('products')
  const [localSelectedProductIds, setLocalSelectedProductIds] = useState<Set<string>>(new Set())
  const [syncingFormatIds, setSyncingFormatIds] = useState<Set<string>>(new Set())
  const [isBrowseModalOpen, setIsBrowseModalOpen] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [rememberedExtras, setRememberedExtras] = useState<Partial<CreateMediaBuyExtras> | undefined>(
    undefined,
  )

  // Restore the last-submitted form values for this conversation so the
  // user doesn't have to retype budget/brand/domain on every retry.
  const rememberKey = conversationId ? `cmb:last:${conversationId}` : null
  useEffect(() => {
    if (!rememberKey) return
    try {
      const raw = localStorage.getItem(rememberKey)
      if (raw) setRememberedExtras(JSON.parse(raw))
    } catch {
      // ignore corrupt cache
    }
  }, [rememberKey])

  // Use media buy product IDs if available, otherwise use local selection
  const hasMediaBuy = mediaBuyData !== null && mediaBuyProductIds.length > 0

  /**
   * Check if a format has been synced by looking at synced creative IDs
   * Creative ID pattern: creative_${formatId}_${timestamp}
   */
  const isFormatSynced = (formatId: string): boolean => {
    return syncedCreatives.some(c => c.creative_id.includes(formatId))
  }

  /**
   * Get sync result for a format if it exists
   */
  const getFormatSyncResult = (formatId: string): CreativeSyncResult | undefined => {
    return syncedCreatives.find(c => c.creative_id.includes(formatId))
  }

  // Remove format from syncing state when sync completes
  useEffect(() => {
    if (syncedCreatives.length > 0) {
      setSyncingFormatIds(prev => {
        const next = new Set(prev)
        // Check each syncing format ID against synced creative IDs
        prev.forEach(formatId => {
          // Check if any synced creative contains this format ID
          // Creative ID pattern: creative_${formatId}_${timestamp}
          if (syncedCreatives.some(result => result.creative_id.includes(formatId))) {
            next.delete(formatId)
          }
        })
        return next
      })
    }
  }, [syncedCreatives])

  // Get products that were selected for media buy (for creative list)
  const mediaBuyProducts = useMemo(() => {
    if (!hasMediaBuy) return []
    return products.filter(p => mediaBuyProductIds.includes(p.product_id))
  }, [products, mediaBuyProductIds, hasMediaBuy])

  // Extract formats for the Sync-creatives view. The newer
  // agentic_buy.create_media_buy ships the exact creatives the buyer must
  // provide on each package via `format_ids_to_provide`. When that's
  // present we trust it as the source of truth (it can be a subset of, or
  // different from, the product's full format catalogue). Otherwise we
  // fall back to the legacy behaviour of unioning every selected product's
  // `format_ids`.
  const mediaBuyFormats = useMemo(() => {
    const formatMap = new Map<string, SalesFormatId>()

    const packageFormats = (mediaBuyData?.packages || []).flatMap(pkg => pkg.format_ids_to_provide || [])
    if (packageFormats.length > 0) {
      packageFormats.forEach(f => {
        if (!f?.id) return
        if (!formatMap.has(f.id)) {
          formatMap.set(f.id, { id: f.id, agent_url: f.agent_url || '' } as SalesFormatId)
        }
      })
      return Array.from(formatMap.values())
    }

    mediaBuyProducts.forEach(product => {
      product.format_ids.forEach(format => {
        if (!formatMap.has(format.id)) {
          formatMap.set(format.id, format)
        }
      })
    })
    return Array.from(formatMap.values())
  }, [mediaBuyProducts, mediaBuyData])

  // Get selected products (for create media buy action)
  const selectedProducts = useMemo(() => {
    return products.filter(p => localSelectedProductIds.has(p.product_id))
  }, [products, localSelectedProductIds])

  // Toggle product selection
  const toggleProduct = (productId: string) => {
    setLocalSelectedProductIds(prev => {
      const next = new Set(prev)
      if (next.has(productId)) {
        next.delete(productId)
      } else {
        next.add(productId)
      }
      return next
    })
  }

  // Select all products
  const selectAll = () => {
    setLocalSelectedProductIds(new Set(products.map(p => p.product_id)))
  }

  // Deselect all products
  const deselectAll = () => {
    setLocalSelectedProductIds(new Set())
  }

  // Open the form modal; actual API call happens after the user fills it in.
  const handleCreateMediaBuy = () => {
    if (selectedProducts.length === 0 || !conversationId) return
    setIsCreateModalOpen(true)
  }

  const handleCreateMediaBuySubmit = async (values: CreateMediaBuyFormValues) => {
    if (selectedProducts.length === 0 || !conversationId) return
    if (rememberKey) {
      try {
        localStorage.setItem(rememberKey, JSON.stringify(values))
        setRememberedExtras(values)
      } catch {
        // localStorage might be disabled; non-fatal
      }
    }
    setIsCreateModalOpen(false)
    await onCreateMediaBuy(selectedProducts, values)
  }

  /**
   * Parse format_id like "card_swiper_300x600" into content_type and size
   * @param formatId - Format ID string (e.g., "card_swiper_300x600", "display_970x250_image")
   * @returns { contentType: string, size: string }
   */
  const parseFormatId = (formatId: string): { contentType: string; size: string } => {
    // Match pattern: size is like "300x600" or "970x250"
    const sizeMatch = formatId.match(/(\d+x\d+)/)
    
    if (sizeMatch) {
      const size = sizeMatch[1]
      // Remove the size from the format to get content_type
      // Also remove trailing underscore or leading underscore around the size
      const contentType = formatId
        .replace(size, '')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
      
      return { contentType, size }
    }
    
    // Fallback: use entire formatId as content_type
    return { contentType: formatId, size: '' }
  }

  // Handle sync creatives - calls API with media buy params
  const handleSyncCreatives = async (format: SalesFormatId) => {
    if (!mediaBuyData || !conversationId) {
      toast.error('No media buy data available')
      return
    }

    // Prefer matching by per-package format_ids_to_provide (new agentic_buy
    // contract). Fall back to product-level format_ids for older payloads
    // that don't carry the per-package list.
    const packagesWithFormatDirect = mediaBuyData.packages.filter(pkg =>
      (pkg.format_ids_to_provide || []).some(f => f.id === format.id),
    )

    let productsWithFormat: SalesProduct[]
    let matchingPackages: typeof mediaBuyData.packages

    if (packagesWithFormatDirect.length > 0) {
      const productIdSet = new Set(packagesWithFormatDirect.map(p => p.product_id))
      productsWithFormat = mediaBuyProducts.filter(p => productIdSet.has(p.product_id))
      matchingPackages = packagesWithFormatDirect
    } else {
      productsWithFormat = mediaBuyProducts.filter(p =>
        p.format_ids.some(f => f.id === format.id),
      )
      if (productsWithFormat.length === 0) {
        toast.error('Could not find product for this format')
        return
      }
      matchingPackages = mediaBuyData.packages.filter(pkg =>
        productsWithFormat.some(p => p.product_id === pkg.product_id),
      )
    }

    if (matchingPackages.length === 0) {
      toast.error('Could not find packages for this format')
      return
    }

    // Parse format_id to extract content_type and size
    const { contentType, size } = parseFormatId(format.id)
    
    // Generate a unique creative_id
    const creativeId = `creative_${format.id}_${Date.now()}`

    // Build selected_templates
    const selectedTemplates = [{
      content_type: contentType,
      sizes: size ? [size] : []
    }]

    // Build creatives array. Stamp the current campaign id onto each
    // creative so the BE can associate the synced creative with this
    // campaign; ad unit id stays null until a creative is actually built.
    const creatives = [{
      creative_id: creativeId,
      name: productsWithFormat[0]?.name || format.id,
      format_id: {
        agent_url: format.agent_url || '',
        id: format.id
      },
      assets: {},
      enzymic_ad_unit_id: null,
      enzymic_campaign_id: campaignId ?? null,
    }]

    // Build assignments — one row per (creative_id, package_id) pair.
    const assignments = matchingPackages.map(pkg => ({
      creative_id: creativeId,
      package_id: pkg.package_id,
    }))

    // Add format to syncing state
    setSyncingFormatIds(prev => new Set(prev).add(format.id))
    
    try {
      console.log('Sync creatives payload:', {
        selected_templates: selectedTemplates,
        creatives,
        assignments
      })

      await conversationApi.syncCreatives(
        conversationId,
        {
          selected_templates: selectedTemplates,
          creatives,
          assignments,
        },
        {
          workflow: 'agentic_buy',
          ...(campaignId != null ? { campaign_id: campaignId } : {}),
        },
      )

      // Toast will be shown when WebSocket response arrives
    } catch (error) {
      console.error('Failed to sync creative:', error)
      toast.error('Failed to sync creative. Please try again.')
      // Remove from syncing state on error
      setSyncingFormatIds(prev => {
        const next = new Set(prev)
        next.delete(format.id)
        return next
      })
    }
  }

  // Get CPM from pricing options
  const getCPM = (product: SalesProduct): string => {
    const cpmOption = product.pricing_options.find(p => p.pricing_model === 'cpm' && p.rate)
    if (cpmOption && cpmOption.rate) {
      return `${cpmOption.currency} ${cpmOption.rate.toFixed(2)}`
    }
    return '-'
  }

  // Get min spend from pricing options
  const getMinSpend = (product: SalesProduct): string => {
    const option = product.pricing_options.find(p => p.min_spend_per_package)
    if (option && option.min_spend_per_package) {
      return `${option.currency} ${option.min_spend_per_package.toLocaleString()}`
    }
    return '-'
  }

  // Build sub-tabs - only show Creative Lists if media buy is complete
  const subTabs = useMemo(() => {
    const tabs = [
      { id: 'products', label: 'Product Lists', icon: ShoppingCart }
    ]
    // Only show Creative Lists tab if media buy is complete
    if (hasMediaBuy) {
      tabs.push({ id: 'creatives', label: 'Creative Lists', icon: Layers })
    }
    return tabs
  }, [hasMediaBuy])

  const handleBrowseSubmit = (message: string) => {
    if (onSendMessage) {
      onSendMessage(message)
    }
    setIsBrowseModalOpen(false)
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-950">
      <BrowseProductsModal
        isOpen={isBrowseModalOpen}
        onClose={() => setIsBrowseModalOpen(false)}
        onSubmit={handleBrowseSubmit}
      />
      <CreateMediaBuyModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        selectedProducts={selectedProducts}
        isSubmitting={isCreating}
        initialValues={rememberedExtras}
        onSubmit={handleCreateMediaBuySubmit}
      />
      {/* Sub-Tab Navigation */}
      <div className="border-b border-gray-200/30 dark:border-slate-800/50 bg-gray-50 dark:bg-slate-900 flex-shrink-0">
        <nav className="flex items-center px-3 py-2 overflow-x-auto">
          {subTabs.map((tab, index) => {
            const Icon = tab.icon
            const isActive = activeSubTab === tab.id
            return (
              <React.Fragment key={tab.id}>
                <button
                  onClick={() => setActiveSubTab(tab.id as 'products' | 'creatives')}
                  className={`relative flex-shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 text-[13px] font-medium rounded-lg transition-colors duration-200 whitespace-nowrap ${
                    isActive
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200/70 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-slate-700/60'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {tab.label}
                  {tab.id === 'creatives' && hasMediaBuy && (
                    <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${isActive ? 'bg-white/20 text-white' : 'bg-green-500 text-white'}`}>
                      {mediaBuyFormats.length}
                    </span>
                  )}
                </button>
                {index < subTabs.length - 1 && (
                  <div className="h-5 w-px bg-gray-300 dark:bg-slate-700 mx-1.5 flex-shrink-0"></div>
                )}
              </React.Fragment>
            )
          })}
          
          {/* Media Buy Status Badge */}
          {hasMediaBuy && mediaBuyData && (
            <div className="ml-auto flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded-full">
                <Check className="h-3 w-3" />
                Media Buy Created
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                {mediaBuyData.media_buy_id}
              </span>
            </div>
          )}
        </nav>
      </div>

      {/* Sub-Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeSubTab === 'products' ? (
          products.length === 0 ? (
            <div className="h-full flex items-center justify-center p-8">
              <div className="text-center max-w-sm">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-2xl bg-gray-100 dark:bg-gray-800 mb-5">
                  <Package className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Products Yet</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Browse and discover advertising products to build your media buy. Use filters to find the right products for your campaign.
                </p>
                <button
                  onClick={() => setIsBrowseModalOpen(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                >
                  <Search className="h-4 w-4" />
                  Browse Products
                </button>
              </div>
            </div>
          ) : (
          <div className="h-full flex flex-col">
            {/* Toolbar */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-center w-8 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={products.length > 0 && (hasMediaBuy ? mediaBuyProductIds.length === products.length : localSelectedProductIds.size === products.length)}
                  ref={(el) => {
                    if (el) {
                      const selectedCount = hasMediaBuy ? mediaBuyProductIds.length : localSelectedProductIds.size
                      el.indeterminate = selectedCount > 0 && selectedCount < products.length
                    }
                  }}
                  onChange={() => {
                    if (hasMediaBuy) return
                    if (localSelectedProductIds.size === products.length) deselectAll()
                    else selectAll()
                  }}
                  disabled={hasMediaBuy}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-transparent disabled:opacity-50"
                />
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <ChevronDown className="h-4 w-4 text-gray-400" />
                {(hasMediaBuy ? mediaBuyProductIds.length : localSelectedProductIds.size) > 0 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {hasMediaBuy ? mediaBuyProductIds.length : localSelectedProductIds.size}
                  </span>
                )}
              </div>
              <div className="flex items-center flex-1 min-w-0">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {hasMediaBuy 
                    ? `${mediaBuyProductIds.length} of ${products.length} selected`
                    : `${localSelectedProductIds.size} of ${products.length} selected`
                  }
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setIsBrowseModalOpen(true)}
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 border border-primary-600 dark:border-primary-500 text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors text-sm font-medium"
                >
                  <Search className="h-3.5 w-3.5" />
                  Browse Products
                </button>
                {!hasMediaBuy && (
                  <button
                    onClick={handleCreateMediaBuy}
                    disabled={localSelectedProductIds.size === 0 || !conversationId || isCreating}
                    className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      localSelectedProductIds.size > 0 && conversationId && !isCreating
                        ? 'bg-primary-600 hover:bg-primary-700 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isCreating ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-3.5 w-3.5" />
                        Create Media Buy
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Table Container */}
            <div className="flex-1 overflow-auto scrollbar-thin">
              {/* Table Header — sticky inside scroll container */}
              <div className="grid grid-cols-[32px_1fr_1.5fr_120px_100px_120px_90px] gap-3 px-4 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-800/60 bg-gray-50/80 dark:bg-gray-900/50 backdrop-blur-sm sticky top-0 z-10">
                <div />
                <div>Product ID</div>
                <div>Name</div>
                <div>Delivery Type</div>
                <div>CPM</div>
                <div>Min Spend</div>
                <div className="text-right">Formats</div>
              </div>

              {/* Table Rows */}
              <div className="divide-y divide-gray-100 dark:divide-gray-800/50">
                {products.map((product) => {
                  const isSelected = hasMediaBuy 
                    ? mediaBuyProductIds.includes(product.product_id)
                    : localSelectedProductIds.has(product.product_id)
                  
                  return (
                    <div
                      key={product.product_id}
                      onClick={() => !hasMediaBuy && toggleProduct(product.product_id)}
                      className={`grid grid-cols-[32px_1fr_1.5fr_120px_100px_120px_90px] gap-3 px-4 py-3 items-center transition-colors group ${
                        hasMediaBuy ? 'cursor-default' : 'cursor-pointer'
                      } ${
                        isSelected 
                          ? 'bg-primary-50/60 dark:bg-primary-900/15' 
                          : hasMediaBuy 
                            ? 'opacity-50' 
                            : 'hover:bg-gray-50 dark:hover:bg-gray-900/40'
                      }`}
                    >
                      {/* Checkbox */}
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          disabled={hasMediaBuy}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-transparent disabled:opacity-50"
                        />
                      </div>

                      {/* Product ID */}
                      <div className="min-w-0">
                        <span className="text-sm font-mono text-gray-600 dark:text-gray-400 truncate block">
                          {product.product_id}
                        </span>
                      </div>

                      {/* Name */}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={product.name}>
                          {product.name}
                        </p>
                        {product.description && product.description !== 'N/A' && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={product.description}>
                            {product.description}
                          </p>
                        )}
                      </div>

                      {/* Delivery Type */}
                      <div className="min-w-0">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          product.delivery_type === 'guaranteed'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                        }`}>
                          {product.delivery_type}
                        </span>
                      </div>

                      {/* CPM */}
                      <div className="min-w-0">
                        <span className="text-sm text-gray-900 dark:text-white">
                          {getCPM(product)}
                        </span>
                      </div>

                      {/* Min Spend */}
                      <div className="min-w-0">
                        <span className="text-sm text-gray-900 dark:text-white">
                          {getMinSpend(product)}
                        </span>
                      </div>

                      {/* Formats */}
                      <div className="text-right">
                        <span className="inline-flex px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded">
                          {product.format_ids.length} format{product.format_ids.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between py-3 px-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {hasMediaBuy 
                    ? `Media buy created with ${mediaBuyProductIds.length} product${mediaBuyProductIds.length !== 1 ? 's' : ''}`
                    : `Showing ${products.length} product${products.length !== 1 ? 's' : ''}`
                  }
                </div>
              </div>
            </div>
          </div>
          )
        ) : (
          /* Creative Lists Sub-Tab - Only shown when media buy is complete */
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-800/50 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Creative Formats for Media Buy
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {mediaBuyFormats.length} format{mediaBuyFormats.length !== 1 ? 's' : ''} from {mediaBuyProducts.length} selected product{mediaBuyProducts.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Formats Grid */}
            <div className="flex-1 overflow-auto p-4 scrollbar-thin">
              {mediaBuyFormats.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <div className="text-5xl mb-4">📭</div>
                  <p>No formats available for selected products</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {mediaBuyFormats.map((format, index) => {
                    const isSyncing = syncingFormatIds.has(format.id)
                    const isSynced = isFormatSynced(format.id)
                    const syncResult = getFormatSyncResult(format.id)
                    
                    return (
                      <motion.div
                        key={format.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className={`bg-white dark:bg-gray-800 border rounded-xl p-4 transition-all ${
                          isSynced 
                            ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10' 
                            : 'border-gray-200 dark:border-gray-700 hover:shadow-md'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <Layers className="h-4 w-4 text-primary-500 flex-shrink-0" />
                              <span className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase">
                                Format
                              </span>
                              {/* Synced Badge */}
                              {isSynced && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-xs font-medium rounded">
                                  <CheckCircle2 className="h-3 w-3" />
                                  {syncResult?.action === 'created' ? 'Created' : syncResult?.action || 'Synced'}
                                </span>
                              )}
                              {/* Syncing Badge */}
                              {isSyncing && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 text-xs font-medium rounded">
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  Syncing...
                                </span>
                              )}
                            </div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white break-all" title={format.id}>
                              {format.id}
                            </p>
                          </div>
                        </div>
                        
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
                          {format.agent_url && (
                            <a
                              href={format.agent_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              <ExternalLink className="h-3 w-3" />
                              Agent URL
                            </a>
                          )}
                          
                          {/* Sync Creative Button - Show different states */}
                          {isSynced ? (
                            <div className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded-lg">
                              <CheckCircle2 className="h-3 w-3" />
                              Creative Synced
                            </div>
                          ) : (
                            <button
                              onClick={() => handleSyncCreatives(format)}
                              disabled={isSyncing}
                              className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                                isSyncing
                                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 cursor-wait'
                                  : 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/30'
                              }`}
                            >
                              {isSyncing ? (
                                <>
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  Syncing...
                                </>
                              ) : (
                                <>
                                  <RefreshCw className="h-3 w-3" />
                                  Sync Creative
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Footer with Media Buy Info */}
            {mediaBuyData && (
              <div className="p-4 border-t border-gray-100 dark:border-gray-800/50 bg-gray-50 dark:bg-gray-900 flex-shrink-0">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4">
                    <span className="text-gray-600 dark:text-gray-400">
                      Media Buy: <span className="font-mono text-gray-900 dark:text-white">{mediaBuyData.media_buy_id}</span>
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">
                      Status: <span className={`font-medium ${mediaBuyData.status === 'submitted' ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'}`}>{mediaBuyData.status}</span>
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">
                      Packages: <span className="font-medium text-gray-900 dark:text-white">{mediaBuyData.packages.length}</span>
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
